/* ============================================================
   SlicingProfilesEditor — 16215cbc: per-printer flattened slicer
   presets (machine/process/filament JSON) for headless Bambu
   Studio CLI slicing. Profiles must be flattened (exported via
   the GUI's «export settings») — the CLI does not resolve
   "inherits" chains (docs/bambu-studio-cli-research.md §3).
   ============================================================ */

import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { api } from '../api/client';

const textareaStyle: CSSProperties = {
  width: '100%',
  minHeight: 96,
  padding: '10px 14px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-separator)',
  fontSize: 'var(--fs-caption)',
  fontFamily: 'monospace',
  background: 'var(--color-surface)',
  color: 'var(--color-text-primary)',
  resize: 'vertical',
};

const FIELDS = [
  { key: 'machine', label: 'machine.json (принтер)', hint: 'Flattened профиль принтера' },
  { key: 'process', label: 'process.json (процесс)', hint: 'Слой/заполнение/скорости' },
  { key: 'filament', label: 'filament.json (филамент)', hint: 'Материал и температура' },
] as const;

type FieldKey = (typeof FIELDS)[number]['key'];

export function SlicingProfilesEditor({ printerId, onSaved }: { printerId: string; onSaved?: () => void }) {
  const [values, setValues] = useState<Record<FieldKey, string>>({ machine: '', process: '', filament: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getPrinterSlicingProfiles(printerId)
      .then((p) => {
        if (cancelled) return;
        setValues({
          machine: p.machineProfileJson ?? '',
          process: p.processProfileJson ?? '',
          filament: p.filamentProfileJson ?? '',
        });
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить профили');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [printerId]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      // Empty slots are sent as null — the backend clears them.
      await api.updatePrinterSlicingProfiles(printerId, {
        machineProfileJson: values.machine.trim() || null,
        processProfileJson: values.process.trim() || null,
        filamentProfileJson: values.filament.trim() || null,
      });
      setNotice('Профили сохранены');
      onSaved?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить профили');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>Загрузка профилей…</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
      <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', margin: 0 }}>
        Flattened JSON-профили Bambu Studio (экспорт из GUI «Export settings»). CLI не разрешает наследование —
        вставляйте полностью развёрнутые файлы. Профили нужны для слайсинга STL/OBJ/STEP; 3MF может нести настройки внутри.
      </p>
      {FIELDS.map((f) => (
        <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
          <span style={{ fontSize: 'var(--fs-caption)', fontWeight: 'var(--fw-medium)' }}>
            {f.label} <span style={{ color: 'var(--color-text-secondary)' }}>· {f.hint}</span>
          </span>
          <textarea
            style={textareaStyle}
            placeholder='{ "printer_model": "BBL-A1", … }'
            value={values[f.key]}
            spellCheck={false}
            onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
          />
      </label>
      ))}
      <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center' }}>
        <button className="btn btn-secondary" disabled={saving} onClick={() => void save()}>
          {saving ? '⏳ …' : 'Сохранить профили'}
        </button>
        {notice && <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>{notice}</span>}
        {error && <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</span>}
      </div>
    </div>
  );
}
